import LinkButton from "~/components/LinkButton"

import { btnWrap } from "./index.css"
import { MetaFunction } from "@remix-run/react"
import {
  articleBodyHighlight,
  articleBodyStyle,
  articleFaqBodyStyle,
  articleImageStyle,
  articleSection,
  bold,
  faqGrid,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  highlightHead,
  orderedList,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import Footer from "~/components/Footer"
import Link from "~/components/Link"
import ScrollyWolly from "~/components/ScrollyWolly"
import { vars } from "~/components/styles/theme.css"
import { page } from "~/shared/styles.css"
import Logo from "~/components/Logo"
import StickyHeader from "~/components/StickyHeader"

export type PreviewImage = {
  url: string
  size: { w: number; h: number }
}

export type Url = { url: string; initial?: PreviewImage | null; loading?: boolean }

export const meta: MetaFunction = () => {
  return [
    {
      title: "Garden Design App - Visualise Your Garden with AI | Rescape AI",
    },
    {
      name: "description",
      content:
        "Upload a photo of your garden and generate professional design ideas in seconds. Rescape AI is the garden design app for homeowners in the UK and US.",
    },
    {
      property: "og:title",
      content: "Garden Design App - Visualise Your Garden with AI | Rescape AI",
    },
    {
      property: "og:description",
      content:
        "Upload a photo of your garden and generate professional design ideas in seconds. Rescape AI is the garden design app for homeowners in the UK and US.",
    },
    {
      property: "og:image",
      content:
        "https://rescapeai.com/transform-your-garden-with-rescape-ai/english-garden-cottage-garden-elements-high-creativity.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/garden-design-app",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Garden Design App | Let AI Effortlessly Remodel Your Garden",
        description:
          "Planning a new garden or remodeling? Rescape AI's garden design app offers a professional-level tool to help you make the most of your outdoor space.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2024-03-28",
        dateModified: "2024-03-28",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: "https://rescapeai.com/landing/modern-garden.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": "https://rescapeai.com/garden-design-app",
        },
        articleSection: ["Technology", "Artificial Intelligence", "Gardening"],
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/landing/modern-garden.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Garden Design App: Visualise Your Garden with AI
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            In recent times, the charm of wildlife-friendly gardens has captured the
            imagination of suburban dwellers across the UK. This growing movement marries
            a love for tranquil and beautiful green spaces with an earnest effort to
            nurture local nature.
          </p>
          <p className={articleBodyStyle}>
            The real task is finding just the right mix: Gardens that please our eyes
            while also inviting all sorts of local birds, mammals, and insects.
          </p>
          <p className={articleBodyStyle}>
            That's where Rescape AI steps in, a garden design app aimed at marrying
            aesthetic grace with ecological hospitality.
          </p>
          <p className={articleBodyStyle}>
            Thanks to its advanced AI, Rescape AI gives homeowners the power to craft
            garden landscapes that catch every visitor&apos;s gaze and provide a sanctuary
            for creatures big and small, making sure your slice of nature buzzes with life
            through all seasons.
          </p>
          <div className={btnWrap} style={{ marginTop: `${3}rem` }}>
            <LinkButton url="/hello/signup" shouldPrefetch>
              Start now
            </LinkButton>
          </div>
        </section>
        <ScrollyWolly
          before={{
            image: "/marketing/before-wildlife-generator.webp",
            title: "Your garden",
            alt: "A customers garden with somewhat bare flower beds",
          }}
          after={{
            image: "/marketing/after-wildlife-generator.webp",
            title: "Designed with AI",
            alt: "A vibrant introduction of lavender, poppies, and other wildflowers by the garden design app evokes feelings of abundance and natural beauty.",
          }}
        />
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>The Dream of a Wildlife-Friendly Garden</h2>
          <p className={articleBodyStyle}>
            Meet Sarah, who pictures her typical suburban yard blooming into a beautiful
            retreat alive with nature's charm.
          </p>
          <p className={articleBodyStyle}>
            She imagines waking to birdsong, watching bees buzz among flowers by day, and
            spotting hedgehogs at twilight. Such a garden isn't just pretty; it breathes
            life into the local environment and supports diverse creatures, aiding
            pollination naturally and warding off pests without chemicals.
          </p>
          <p className={articleBodyStyle}>
            Sarah is on board but wonders if such an oasis is achievable amid suburbia’s
            hustle.
          </p>
          <p className={articleBodyStyle}>
            The trick lies in plant choices: think lavender for buzzing bees or foxgloves
            that butterflies love; bushes dotted with berries to feed the birds; shrubs
            offering shelter for small wanderers like hedgehogs.
          </p>
          <p className={articleBodyStyle}>
            Yet blending these picks so they attract wildlife year-round and look good
            doing it is where Sarah hits a snag.
          </p>
          <p className={articleBodyStyle}>
            Without expert know-how about{" "}
            <Link href="/article/back-garden-ideas">crafting modern gardens</Link> or
            nurturing plants that invite friendly critters over seasons, this vision
            remains just out of reach.
          </p>
          <p className={articleBodyStyle}>
            This common dilemma underscores the need for a tool that bridges the gap
            between aspiration and reality, guiding homeowners in creating spaces where
            nature and beauty coexist seamlessly.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={`${h2Style} ${highlightHead}`}>
            How Rescape AI Comes to the Rescue
          </h2>
          <p className={`${articleBodyStyle} ${articleBodyHighlight}`}>
            Rescape AI is the perfect solution for garden enthusiasts like Sarah, who's
            keen to craft an outdoor space that invites nature in while{" "}
            <Link href="/article/nature-in-contemporary-design">keeping it chic.</Link>
          </p>
        </section>
        <img
          src="/marketing/wildlife-friendly-meadow.webp"
          alt="Our garden design app can completely overhaul your garden, designing a wildlife friendly meadow complete with delightful pathways through."
          className={articleImageStyle}
          loading="lazy"
        />
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Smart Garden Designs</h2>
          <p className={articleBodyStyle}>
            Rescape AI uses an AI-powered image generator to bring your green fantasies to
            life with professional design proposals.
          </p>
          <p className={articleBodyStyle}>
            Just upload a photo and watch as ideas bloom into customised plans that not
            only look good but also welcome birds and bees alike—thanks to AI providing
            high-quality, lifelike suggestions blending beauty with practicality.
          </p>
          <h2 className={h2Style}>Real Visual Representation</h2>
          <p className={articleBodyStyle}>
            The Rescape AI offers more than mere visual representations of your garden. It
            harnesses the power of AI to suggest professional landscaping choices,
            ensuring your garden not only looks good but also supports wildlife all year
            round.
          </p>
          <p className={articleBodyStyle}>
            With the garden design app you're equipped with expertly designed garden
            layouts that are both beautiful and functional, making your outdoor space a
            thriving habitat for nature's visitors in every season.
          </p>
          <h2 className={h2Style}>Creative Design Exploration</h2>
          <p className={articleBodyStyle}>
            What really makes Rescape AI stand out is its embrace of creative freedom. You
            get infinite tries at reimagining one single photograph until everything
            clicks into place. This encourages homeowners to tweak designs without limits
            until they capture their ideal mix where natural allure meets sleek
            landscaping finesse.
          </p>
          <p className={articleBodyStyle}>
            With tools like Rescape AI at our fingertips, creating gardens that are both
            stunningly modern yet teeming with local birds, squirrels, and bees isn't just
            wishful thinking anymore.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={`${h2Style} ${highlightHead}`}>
            Creating Your Wildlife Garden with Rescape AI - Tips and Tricks
          </h2>
          <p className={`${articleBodyStyle} ${articleBodyHighlight}`}>
            Starting your journey to craft a wildlife-friendly garden with a garden design
            app like Rescape AI is both thrilling and user-friendly. Use this robust tool
            to shape an outdoor area that dazzles the eyes and welcomes local fauna.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>How-to Guide for Rescape AI</h2>
          <ol className={orderedList}>
            <li>
              <b className={bold}>Photograph Your Space:</b> Snap crisp, well-lit photos
              of your garden area—your privacy is valued here; those shots stay yours.
            </li>
            <li>
              <b className={bold}>Upload with Details:</b> Send pictures to Rescape AI,
              picking from the menu what you envision for your space's style. If needed,
              describe specifics like "a bird bath in the middle of the yard," or mention
              your goal of attracting specific wildlife.
            </li>
            <li>
              <b className={bold}>Explore and Refine:</b> Rescape AI will generate
              thousands of variations, allowing you to explore different designs. Use
              these as inspiration to refine your vision, focusing on elements that
              enhance both aesthetics and wildlife attraction.
            </li>
          </ol>
          <h2 className={h2Style}>Planning a Variety-Filled Retreat for Animals</h2>
          <ul className={unorderedList}>
            <li>Lavender & Foxgloves call bees and butterflies.</li>
            <li>Berry Bushes are bird treats.</li>
            <li>Dense Shrubs cosy-up hedgehogs and small critters.</li>
            <li>Wildflowers bedazzle insects galore, boosting diversity.</li>
          </ul>
          <p className={articleBodyStyle}>
            Rescape AI pieces together habitats live-in ready for critters large and small
            alike. From layout mapping down to optimal plant picks, with visually stunning
            results fusing practicality alongside beauty all guided by tech-savvy smarts
            at your fingertips.
          </p>
          <h2 className={h2Style}>
            Embracing Nature's Symphony in Your Backyard with Rescape AI
          </h2>
          <p className={articleBodyStyle}>
            Just because you don't live in the countryside doesn't mean your backyard
            can't be a haven for local wildlife. And with Rescape AI, lacking a{" "}
            <Link href="/ai-yard-design">landscaper's expertise</Link> is no
            barrier to crafting your own urban oasis.
          </p>
          <p className={articleBodyStyle}>
            Unlock the door to a world where beauty intertwines with nature—start
            designing your wildlife sanctuary today with <Link href="/">Rescape</Link>.
          </p>
        </section>
        <div className={btnWrap} style={{ marginTop: `${3}rem` }}>
          <LinkButton url="/hello/signup" shouldPrefetch>
            Join
          </LinkButton>
        </div>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Frequently asked questions.</h2>
          <div className={faqGrid}>
            <div>
              <h3 className={h3Style}>
                What level of customisation is available for the generated designs?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Users have the flexibility to select their preferred style and specific
                features. Additionally, the app accepts custom input for detailed
                instructions, allowing for a more personalized approach. A unique feature
                is the ability to paint a mask over areas in the image that users wish to
                preserve, such as existing garden elements or structures.
              </p>
            </div>
            <div>
              <h3 className={h3Style}>
                Can the app integrate existing features into the design?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Yes, the app can seamlessly integrate existing garden features into the
                new design. Users can utilize the masking tool to paint over any area of
                the image they wish to preserve. The mask ensures that these features are
                not altered during the design process.
              </p>
            </div>
            <div>
              <h3 className={h3Style}>
                Are there any in app purchases or subscription fees required?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Users can choose to buy credits for generating images, and subscribing to
                the service offers discounts on generated images. One credit generates one
                image.
              </p>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  )
}

export default Index
